import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldError,
} from './ui/field';
import { Input } from './ui/input';
import { ViewState } from '../types';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { auth } from '../services/firebase';

// Base URL for the FDW Cloud Run backend (fdwApi). Confirm this matches
// wherever the rest of the app already points its API calls — if a
// shared constant already exists elsewhere in this frontend, use that
// instead of redeclaring it here.
//const FDW_API_BASE = 'https://fdwapi-x2efogv6ja-uc.a.run.app';
const FDW_BASE = 'https://fivedollarworkday.com';

interface LoginPageProps {
  onNavigate: (view: ViewState) => void;
}

// resumeSession present means this person arrived via an MCP client's
// OAuth redirect (see index.ts's /mcp/login, which now does nothing but
// forward here with this param) — not a direct visit to the site. Read
// once at mount; not expected to change during the page's lifetime.
function useResumeParams(): { session: string | null; error: string | null } {
  const params = new URLSearchParams(window.location.search);
  return {
    session: params.get('resumeSession'),
    error: params.get('error'),
  };
}

// RESTYLED — markup only, swapped to real Field/FieldGroup/FieldLabel/
// FieldError primitives (field.tsx, confirmed real source) to match the
// visual language of the shadcn login-form.tsx/signup-form.tsx
// reference. All logic below (handleSubmit, resumeSession branching,
// !auth local-dev fallback, error-code mapping, mode toggle) is
// unchanged from the original — same behavior, restructured layout
// only. Kept as one component with internal mode toggle, not split
// into separate LoginForm/SignupForm components, since nothing here
// requires the split and the existing resumeSession-aware copy
// branching would need to be duplicated across two files for no
// behavioral gain.
export const LoginPage: React.FC<LoginPageProps> = ({ onNavigate }) => {
  const { session: resumeSession, error: resumeError } = useResumeParams();

  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(resumeError);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (!auth) {
        // Fallback execution for local testing without Firebase config.
        // Not resume-session-aware — this path only supports the plain
        // local-dev mock, same as before this change.
        console.warn(`Running in fallback mode: Mocking email/password ${mode}`);
        const mockUser = {
          uid: 'user_admin',
          displayName: 'Five Dollar Workday',
          email: email,
          photoURL: 'https://ui-avatars.com/api/?name=Five+Dollar',
          isPro: true,
          isAdmin: true
        };
        localStorage.setItem('ctt_user', JSON.stringify(mockUser));
        window.location.href = '/dashboard';
        return;
      }

      // Firebase defaults to browserLocalPersistence already — no explicit
      // persistence call needed.
      const cred = mode === 'signup'
        ? await createUserWithEmailAndPassword(auth, email, password)
        : await signInWithEmailAndPassword(auth, email, password);

      if (resumeSession) {
        // This person arrived via an MCP client. Exchange the freshly
        // verified Firebase ID token back into the pending OAuth session —
        // mirrors what index.ts's old /mcp/login/submit did server-side,
        // now triggered from the client after Firebase auth completes here.
        const idToken = await cred.user.getIdToken();
        const resumeRes = await fetch(`${FDW_BASE}/mcp/resume-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken, session: resumeSession }),
        });

        if (!resumeRes.ok) {
          const body = await resumeRes.json().catch(() => ({}));
          throw new Error(body.error || 'Could not resume your connection. Please try again.');
        }

        // Redirect back into the OAuth chain — mirrors the old
        // /mcp/auth/callback redirect, now triggered from the client
        // instead of a server-rendered form submit.
        window.location.href = `${FDW_BASE}/mcp/auth/callback?session=${encodeURIComponent(resumeSession)}`;
        return;
      }

      // Ordinary direct visit — no assistant waiting. Navigation is
      // handled by App.tsx detecting the auth state change when already
      // on /dashboard, but push explicitly so the user lands there
      // either way if they arrived via /login.
      onNavigate('USER_DASHBOARD');
    } catch (err: any) {
      console.error(`${mode} failed`, err);
      if (err.code === 'auth/invalid-credential') {
        setError('Incorrect email or password. Please try again.');
      } else if (err.code === 'auth/email-already-in-use') {
        setError('An account already exists for that email.');
        setMode('signin');
      } else if (err.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.');
      } else if (err.code === 'auth/invalid-email') {
        setError('That email address doesn\'t look right.');
      } else {
        setError(err.message || `Failed to ${mode === 'signup' ? 'create account' : 'sign in'}. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center p-4 bg-brand-surface">
      <div className="w-full max-w-md bg-white p-8 md:p-12 shadow-soft rounded-2xl border border-brand-border">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="p-3 mb-6 bg-white border border-brand-border rounded-xl shadow-sm">
            <img
              src="/factory.png"
              alt="Five Dollar Workday Logo"
              className="w-8 h-8 object-contain"
            />
          </div>

          {resumeSession ? (
            <>
              <h1 className="text-2xl md:text-3xl font-medium mb-2 font-display text-brand-black">
                {mode === 'signup' ? 'Create your FDW account' : 'Sign in to continue'}
              </h1>
              <p className="text-brand-charcoal font-light leading-relaxed">
                Your AI assistant is waiting to connect.{' '}
                {mode === 'signup'
                  ? 'Create an account and you\'ll be sent right back to finish connecting.'
                  : 'Sign in and you\'ll be sent right back to finish connecting.'}
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl md:text-3xl font-medium mb-2 font-display text-brand-black">
                {mode === 'signup' ? 'Create your account' : 'Manage your AI profile'}
              </h1>
              <p className="text-brand-charcoal font-light leading-relaxed">
                {mode === 'signup'
                  ? 'Set up your FDW account to publish a business, or just to message one.'
                  : 'Sign in to update your single source of truth, configure your AI rules, and control how agents engage with your business.'}
              </p>
            </>
          )}
        </div>

        <form onSubmit={handleSubmit} autoComplete="on">
          <FieldGroup>
            <FieldError errors={error ? [{ message: error }] : undefined} />

            <Field>
              <FieldLabel htmlFor="login-email">Email</FieldLabel>
              <Input
                id="login-email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="username"
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="login-password">Password</FieldLabel>
              <Input
                id="login-password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={mode === 'signup' ? 6 : undefined}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
            </Field>

            <Field>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 text-base relative flex items-center justify-center gap-3"
                variant="primary"
              >
                {isLoading
                  ? (mode === 'signup' ? 'Creating account...' : 'Signing in...')
                  : (mode === 'signup' ? 'Create account' : 'Sign In')}
              </Button>
            </Field>
          </FieldGroup>
        </form>

        <p className="text-center text-sm text-brand-charcoal mt-6">
          {mode === 'signup' ? (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => { setMode('signin'); setError(null); }}
                className="underline hover:text-brand-black"
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              No account yet?{' '}
              <button
                type="button"
                onClick={() => { setMode('signup'); setError(null); }}
                className="underline hover:text-brand-black"
              >
                Create one
              </button>
            </>
          )}
        </p>

        <div className="mt-8 pt-8 border-t border-brand-border text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-brand-gray mb-4">
            <ShieldCheck className="w-4 h-4" />
            <span>Secure Authentication</span>
          </div>
          <p className="text-xs text-brand-gray">
            By continuing, you agree to the <a href="/terms" className="underline hover:text-brand-black">Terms of Service</a> and <a href="/privacy" className="underline hover:text-brand-black">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
};